import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { DbAgentClient } from './db-agent.client';
import { BackupAlertService } from './backup-alert.service';
import { LocalBackupService } from './local-backup.service';

export interface QuotaVerdict {
  admitted: boolean;
  /** Why, in words an operator can act on. Surfaced verbatim in the UI. */
  reason: string;
  usedBytes: bigint;
  quotaBytes: bigint;
  projectedBytes: bigint;
  usedPct: number;
  projectedPct: number;
}

/** 1 GiB — the client's budget for this deployment. */
export const DEFAULT_QUOTA_BYTES = 1073741824n;

/**
 * Enforces the repository byte budget.
 *
 * WHY THIS EXISTS AT ALL
 *
 * pgBackRest expires by count and age. It has no concept of "you may use one
 * gigabyte", so on a fixed budget nothing stops the repository growing until
 * writes to R2 start failing — at which point `archive-push` fails, WAL backs
 * up locally, and the system reports healthy backups right up to the moment it
 * has none. A backup system that fills its own bucket is worse than no backup
 * system, because it is silently confident.
 *
 * So admission happens HERE, before a run starts, against a projection of what
 * the run will add. Refusing a backup is a bad outcome; discovering mid-restore
 * that the last three weeks of WAL were never uploaded is a much worse one.
 *
 * THE ESTIMATE
 *
 * Projected size comes from the median of recent completed runs of the same
 * type, not the mean: one anomalous full backup (a migration that rewrote a
 * large table) would drag a mean upward for weeks and refuse everything after
 * it. Median plus a 20% margin tracks the real trend and tolerates outliers.
 * With no history, we fall back to a conservative fraction of the quota rather
 * than assuming zero.
 */
@Injectable()
export class BackupQuotaService {
  private readonly logger = new Logger(BackupQuotaService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly agent: DbAgentClient,
    private readonly alerts: BackupAlertService,
    private readonly local: LocalBackupService
  ) {}

  /**
   * May this run start?
   *
   * Called before every backup, snapshot and drill. The verdict is recorded on
   * the run when it refuses, so "why did last night's backup not happen?" has
   * an answer in the same table as everything else.
   */
  async admit(repositoryId: string, backupType: string): Promise<QuotaVerdict> {
    const repo = await this.prisma.backupRepository.findUnique({ where: { id: repositoryId } });
    if (!repo) {
      return this.verdict(false, 'repository not found', 0n, 0n, 0n);
    }

    // Zero means unbounded. Only ever correct for the local repo, whose real
    // limit is the disk, and which has its own headroom check in the health
    // service.
    if (repo.quotaBytes === 0n) {
      return this.verdict(true, 'no quota configured', repo.usedBytes, 0n, repo.usedBytes);
    }

    const estimate = await this.estimate(repo.id, backupType);
    const projected = repo.usedBytes + estimate;
    const hardLimit = (repo.quotaBytes * BigInt(repo.hardLimitPct)) / 100n;

    if (projected > hardLimit) {
      // Try to make room before giving up: an expire may free exactly the
      // space this run needs, and a backup that succeeds after pruning is
      // strictly better than one that never ran.
      const freed = await this.pruneToFit(repo.id, projected - hardLimit);
      if (freed > 0n) {
        const after = await this.prisma.backupRepository.findUnique({ where: { id: repo.id } });
        const afterProjected = (after?.usedBytes ?? repo.usedBytes) + estimate;
        if (afterProjected <= hardLimit) {
          return this.verdict(
            true,
            `admitted after pruning ${this.mb(freed)} MB`,
            after?.usedBytes ?? repo.usedBytes,
            repo.quotaBytes,
            afterProjected
          );
        }
      }

      await this.alerts.raise({
        kind: 'quota_hard',
        severity: 'critical',
        title: `Backup repository "${repo.name}" is at its hard limit`,
        detail: {
          usedBytes: repo.usedBytes.toString(),
          quotaBytes: repo.quotaBytes.toString(),
          projectedBytes: projected.toString(),
          backupType,
          // The operator's actual options, spelled out. An alert that states a
          // problem without stating the levers is a page nobody can action.
          remedies: [
            'raise quotaBytes if the budget allows',
            'reduce retentionFull/retentionDiff/retentionDays',
            'raise archive_timeout — WAL segment count is usually the largest term',
          ],
        },
      });

      return this.verdict(
        false,
        `refused: ${this.mb(projected)} MB projected exceeds the ${repo.hardLimitPct}% hard limit ` +
          `(${this.mb(hardLimit)} MB of ${this.mb(repo.quotaBytes)} MB)`,
        repo.usedBytes,
        repo.quotaBytes,
        projected
      );
    }

    const softLimit = (repo.quotaBytes * BigInt(repo.softLimitPct)) / 100n;
    if (projected > softLimit) {
      await this.alerts.raise({
        kind: 'quota_soft',
        severity: 'warning',
        title: `Backup repository "${repo.name}" is past ${repo.softLimitPct}% of its budget`,
        detail: {
          usedBytes: repo.usedBytes.toString(),
          quotaBytes: repo.quotaBytes.toString(),
          projectedBytes: projected.toString(),
        },
      });
    }

    return this.verdict(true, 'admitted', repo.usedBytes, repo.quotaBytes, projected);
  }

  /**
   * Median size of recent completed runs of this type, plus 20%.
   *
   * Median rather than mean: see the class comment. Twenty percent rather than
   * an exact figure because a differential grows with distance from its full,
   * so the next one is reliably a little larger than the last.
   */
  private async estimate(repositoryId: string, backupType: string): Promise<bigint> {
    const recent = await this.prisma.backupRun.findMany({
      where: { repositoryId, backupType, status: 'completed', repoSizeBytes: { gt: 0 } },
      orderBy: { startedAt: 'desc' },
      take: 7,
      select: { repoSizeBytes: true },
    });

    if (!recent.length) {
      // No history. Assume a full costs 15% of quota and anything else 5% —
      // deliberately pessimistic, because the failure mode of guessing low is
      // an over-full bucket and the failure mode of guessing high is one
      // deferred backup.
      const repo = await this.prisma.backupRepository.findUnique({ where: { id: repositoryId } });
      const quota = repo?.quotaBytes ?? DEFAULT_QUOTA_BYTES;
      return (quota * BigInt(backupType === 'full' ? 15 : 5)) / 100n;
    }

    const sorted = recent.map((r) => r.repoSizeBytes).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    const median = sorted[Math.floor(sorted.length / 2)];
    return (median * 120n) / 100n;
  }

  /**
   * Ask pgBackRest to expire, and report how much that freed.
   *
   * Deliberately does NOT delete anything itself. pgBackRest understands which
   * WAL is still required by a retained backup; a byte-counting prune that did
   * not would happily delete the segments that make the remaining backups
   * restorable, producing a repository that looks healthy and recovers nothing.
   */
  private async pruneToFit(repositoryId: string, _shortfall: bigint): Promise<bigint> {
    const repo = await this.prisma.backupRepository.findUnique({ where: { id: repositoryId } });
    if (!repo || !this.agent.isConfigured()) return 0n;

    const target = await this.prisma.backupTarget.findFirst({ where: { isActive: true } });
    if (!target) return 0n;

    const before = repo.usedBytes;
    try {
      await this.agent.call('backup.expire', { stanza: target.stanza, repo: repo.repoIndex });
    } catch (err) {
      this.logger.warn(`expire failed on ${repo.code}: ${(err as Error).message}`);
      return 0n;
    }

    await this.refresh(target.stanza);
    const after = await this.prisma.backupRepository.findUnique({ where: { id: repositoryId } });
    const freed = before - (after?.usedBytes ?? before);
    return freed > 0n ? freed : 0n;
  }

  /**
   * Recompute usage from what the repository actually contains.
   *
   * Derived from `pgbackrest info` plus our own WAL rows rather than from a
   * running total we increment: a counter that drifts is worse than no counter,
   * because it is trusted. This is the same reasoning the media storage
   * subsystem already applies to its quota.
   */
  @Cron('*/20 * * * *')
  async refreshAll(): Promise<void> {
    if (!this.agent.isConfigured()) return;
    const targets = await this.prisma.backupTarget.findMany({ where: { isActive: true } });
    for (const target of targets) {
      await this.refresh(target.stanza).catch((err) =>
        this.logger.warn(`quota refresh failed for ${target.stanza}: ${err.message}`)
      );
    }
  }

  async refresh(stanza: string): Promise<void> {
    const repos = await this.prisma.backupRepository.findMany({ where: { isActive: true, kind: 's3' } });

    // No agent means no pgBackRest to ask — but the BUCKET is still perfectly
    // reachable, and counting it directly is both possible and correct.
    //
    // The earlier version called the agent unconditionally and, when it was
    // absent, reported "Cloudflare R2 is unreachable" as a CRITICAL alert. That
    // is factually wrong: R2 was fine, the agent simply was not installed.
    // Alerts that misname the fault are worse than no alert, because the next
    // real repo_unreachable gets read as "that thing again".
    if (!this.agent.isConfigured()) {
      await this.refreshFromObjectStore(repos);
      return;
    }

    for (const repo of repos) {
      let backupBytes = 0n;
      let objectCount = 0;

      try {
        const info = await this.agent.backupInfo(stanza, repo.repoIndex);
        for (const stanzaInfo of info) {
          for (const backup of stanzaInfo.backup ?? []) {
            backupBytes += BigInt(backup.info?.repository?.size ?? 0);
            objectCount++;
          }
        }
      } catch (err) {
        await this.prisma.backupRepository.update({
          where: { id: repo.id },
          data: { status: 'unreachable', lastError: (err as Error).message, lastCheckedAt: new Date() },
        });
        await this.alerts.raise({
          kind: 'repo_unreachable',
          severity: 'critical',
          title: `Backup repository "${repo.name}" is unreachable`,
          detail: { error: (err as Error).message },
        });
        continue;
      }

      // WAL is tracked in our own table because pgbackrest info reports only
      // the archive range, not its byte size — and the byte size is exactly
      // what the budget is denominated in.
      const wal = await this.prisma.walArchiveSegment.aggregate({
        where: { repositoryId: repo.id, status: 'uploaded' },
        _sum: { compressedBytes: true },
      });
      const walBytes = BigInt(wal._sum.compressedBytes ?? 0);
      const usedBytes = backupBytes + walBytes;

      const pct = repo.quotaBytes > 0n ? Number((usedBytes * 100n) / repo.quotaBytes) : 0;
      const status =
        repo.quotaBytes > 0n && pct >= repo.hardLimitPct
          ? 'over_quota'
          : repo.quotaBytes > 0n && pct >= repo.softLimitPct
            ? 'degraded'
            : 'ok';

      await this.prisma.backupRepository.update({
        where: { id: repo.id },
        data: {
          usedBytes,
          backupBytes,
          walBytes,
          objectCount,
          status,
          lastError: null,
          lastCheckedAt: new Date(),
        },
      });

      if (status === 'ok') {
        await this.alerts.resolve('quota_soft', null);
        await this.alerts.resolve('quota_hard', null);
        await this.alerts.resolve('repo_unreachable', null);
      }
    }
  }

  /**
   * Count usage by listing the bucket, for hosts with no agent.
   *
   * Same derived-not-incremented rule as the pgBackRest path: a running total
   * that drifts is worse than no total, because it is trusted.
   */
  private async refreshFromObjectStore(repos: Array<{ id: string; quotaBytes: bigint; softLimitPct: number; hardLimitPct: number }>): Promise<void> {
    if (!this.local.isAvailable()) return;

    const usage = await this.local.usage();
    // All three prefixes: dumps, base backups and WAL.
    const used = usage.dumpBytes + usage.baseBytes + usage.walBytes;

    for (const repo of repos) {
      const pct = repo.quotaBytes > 0n ? Number((used * 100n) / repo.quotaBytes) : 0;

      await this.prisma.backupRepository.update({
        where: { id: repo.id },
        data: {
          usedBytes: used,
          backupBytes: usage.dumpBytes + usage.baseBytes,
          walBytes: usage.walBytes,
          objectCount: usage.objectCount,
          status:
            repo.quotaBytes > 0n && pct >= repo.hardLimitPct
              ? 'over_quota'
              : repo.quotaBytes > 0n && pct >= repo.softLimitPct
                ? 'degraded'
                : 'ok',
          lastCheckedAt: new Date(),
          lastError: null,
        },
      });
    }

    // The repository was reachable after all, so clear any stale claim that it
    // was not.
    await this.alerts.resolve('repo_unreachable', null);
  }

  private verdict(
    admitted: boolean,
    reason: string,
    usedBytes: bigint,
    quotaBytes: bigint,
    projectedBytes: bigint
  ): QuotaVerdict {
    return {
      admitted,
      reason,
      usedBytes,
      quotaBytes,
      projectedBytes,
      usedPct: quotaBytes > 0n ? Number((usedBytes * 100n) / quotaBytes) : 0,
      projectedPct: quotaBytes > 0n ? Number((projectedBytes * 100n) / quotaBytes) : 0,
    };
  }

  private mb(bytes: bigint): string {
    return (Number(bytes) / (1024 * 1024)).toFixed(1);
  }
}
